import type {
	AuthorizeNetBankData,
	AuthorizeNetCardData,
	AuthorizeNetDispatchData,
	AuthorizeNetDispatchResponse,
	OpaqueData,
	PaymentGatewayConfig,
	PaymentMethodFormData,
} from '../../types/payment.types';
import {
	PaymentGatewayError,
	PaymentMethodTypeEnum,
} from '../../types/payment.types';
import { ApiService } from '../ApiService';

// Global Accept.js interface
declare global {
	interface Window {
		Accept: {
			dispatchData: (
				secureData: AuthorizeNetDispatchData,
				responseHandler: (response: AuthorizeNetDispatchResponse) => void,
			) => void;
		};
	}
}

export class AuthorizeNetFrontendService {
	private config: PaymentGatewayConfig | null = null;
	private isInitialized = false;
	private scriptLoaded = false;

	/**
	 * Initialize the service by loading configuration and Accept.js script
	 */
	/**
	 * @param externalConfig Optional pre-fetched gateway config. When provided,
	 *   skips the internal ApiService call — allows non-CRM consumers (e.g. portal)
	 *   to supply config fetched through their own API layer.
	 */
	async initialize(externalConfig?: PaymentGatewayConfig): Promise<void> {
		if (this.isInitialized) {
			console.debug('[AuthorizeNet] Already initialized, skipping');

			return;
		}

		try {
			console.debug('[AuthorizeNet] Starting initialization...');

			// 1. Get configuration — use external config if provided, otherwise fetch from backend
			this.config = externalConfig ?? (await this.getConfiguration());
			console.debug('[AuthorizeNet] Config received:', {
				environment: this.config?.environment,
				hasClientKey: Boolean(this.config?.publicCredentials?.clientKey),
				hasApiLoginID: Boolean(this.config?.publicCredentials?.apiLoginID),
			});

			// 2. Load Accept.js script
			await this.loadAcceptJSScript();
			console.debug(
				'[AuthorizeNet] Script loaded, window.Accept available:',
				Boolean(window.Accept),
			);

			this.isInitialized = true;
			console.debug('[AuthorizeNet] Initialization complete');
		} catch (error) {
			console.error('[AuthorizeNet] Initialization failed:', error);
			throw new PaymentGatewayError(
				'Failed to initialize Authorize.net service',
				'INIT_ERROR',
				error,
			);
		}
	}

	/**
	 * Tokenize credit card data using Accept.js
	 */
	async tokenizeCardData(formData: PaymentMethodFormData): Promise<OpaqueData> {
		console.debug('[AuthorizeNet] tokenizeCardData called', {
			isInitialized: this.isInitialized,
			hasConfig: Boolean(this.config),
			hasWindowAccept: Boolean(window.Accept),
		});

		if (!this.isInitialized || !this.config) {
			console.error('[AuthorizeNet] Service not initialized');
			throw new PaymentGatewayError(
				'Service not initialized. Call initialize() first.',
				'NOT_INITIALIZED',
			);
		}

		if (!window.Accept) {
			console.error(
				'[AuthorizeNet] window.Accept is undefined at tokenization time',
				{
					isInitialized: this.isInitialized,
					scriptLoaded: this.scriptLoaded,
				},
			);
			throw new PaymentGatewayError(
				'Accept.js not loaded properly',
				'SCRIPT_NOT_LOADED',
			);
		}

		// Parse expiration date - handles MMYY, MMYYYY, or MM[separator]YY/YYYY
		const expDate = formData.expirationDate;
		let expMonth: string;
		let expYearPart: string;

		if (/^\d{4}$/.test(expDate)) {
			// MMYY format (4 digits)
			expMonth = expDate.slice(0, 2);
			expYearPart = expDate.slice(2);
		} else if (/^\d{6}$/.test(expDate)) {
			// MMYYYY format (6 digits)
			expMonth = expDate.slice(0, 2);
			expYearPart = expDate.slice(2);
		} else {
			// MM[separator]YY or MM[separator]YYYY
			expMonth = expDate.slice(0, 2);
			expYearPart = expDate.replace(/^\d{2}\D/, '');
		}

		const expYear = expYearPart.length === 2 ? `20${expYearPart}` : expYearPart;

		// Transform form data to Authorize.net format
		const cardData: AuthorizeNetCardData = {
			cardNumber: formData.cardNumber.replace(/\s/g, ''), // Remove spaces
			month: expMonth,
			year: expYear,
			cardCode: formData.securityCode,
			zip: formData.zipCode,
			fullName: formData.nameOnCard,
		};

		// Remove empty/undefined values
		const cleanCardData = this.removeEmptyValues(cardData);

		const dispatchData: AuthorizeNetDispatchData = {
			authData: {
				clientKey: this.config.publicCredentials.clientKey ?? '',
				apiLoginID: this.config.publicCredentials.apiLoginID ?? '',
			},
			cardData: cleanCardData,
		};

		return new Promise((resolve, reject) => {
			// Add a small delay before calling dispatchData to ensure Accept.js is fully ready
			// This helps prevent "Accept.js is not loaded correctly" errors
			setTimeout(() => {
				try {
					window.Accept.dispatchData(
						dispatchData,
						(response: AuthorizeNetDispatchResponse) => {
							if (response.messages?.resultCode === 'Error') {
								const errorMessage =
									response.messages.message?.[0]?.text ||
									'Unknown error occurred';
								const errorCode =
									response.messages.message?.[0]?.code || 'UNKNOWN_ERROR';

								reject(
									new PaymentGatewayError(
										`Authorize.net error: ${errorMessage}`,
										errorCode,
										response,
									),
								);
								return;
							}

							if (!response.opaqueData) {
								reject(
									new PaymentGatewayError(
										'No opaque data received from Authorize.net',
										'NO_OPAQUE_DATA',
										response,
									),
								);
								return;
							}

							resolve(response.opaqueData);
						},
					);
				} catch (error) {
					console.error('[AuthorizeNet] Error calling dispatchData:', error);
					reject(
						new PaymentGatewayError(
							'Failed to tokenize payment data',
							'DISPATCH_ERROR',
							error,
						),
					);
				}
			}, 100);
		});
	}

	/**
	 * Tokenize eCheck bank account data using Accept.js
	 */
	async tokenizeBankData(formData: PaymentMethodFormData): Promise<OpaqueData> {
		if (!this.isInitialized || !this.config) {
			throw new PaymentGatewayError(
				'Service not initialized. Call initialize() first.',
				'NOT_INITIALIZED',
			);
		}

		if (!window.Accept) {
			throw new PaymentGatewayError(
				'Accept.js not loaded properly',
				'SCRIPT_NOT_LOADED',
			);
		}

		// Transform form data to Authorize.net bank format
		const bankData: AuthorizeNetBankData = {
			accountNumber: formData.accountNumber.trim(),
			routingNumber: formData.routingNumber.trim(),
			nameOnAccount: formData.nameOnAccount.trim(),
			accountType: formData.accountType,
			zip: formData.zipCode.trim(),
		};

		// Remove empty/undefined values
		const cleanBankData = this.removeEmptyValues(bankData);

		const dispatchData: AuthorizeNetDispatchData = {
			authData: {
				clientKey: this.config.publicCredentials.clientKey ?? '',
				apiLoginID: this.config.publicCredentials.apiLoginID ?? '',
			},
			bankData: cleanBankData,
		};

		return new Promise((resolve, reject) => {
			// Add a small delay before calling dispatchData to ensure Accept.js is fully ready
			// This helps prevent "Accept.js is not loaded correctly" errors
			setTimeout(() => {
				try {
					window.Accept.dispatchData(
						dispatchData,
						(response: AuthorizeNetDispatchResponse) => {
							if (response.messages?.resultCode === 'Error') {
								const errorMessage =
									response.messages.message?.[0]?.text ||
									'Unknown error occurred';
								const errorCode =
									response.messages.message?.[0]?.code || 'UNKNOWN_ERROR';

								reject(
									new PaymentGatewayError(
										`Authorize.net error: ${errorMessage}`,
										errorCode,
										response,
									),
								);
								return;
							}

							if (!response.opaqueData) {
								reject(
									new PaymentGatewayError(
										'No opaque data received from Authorize.net',
										'NO_OPAQUE_DATA',
										response,
									),
								);
								return;
							}

							resolve(response.opaqueData);
						},
					);
				} catch (error) {
					console.error('[AuthorizeNet] Error calling dispatchData:', error);
					reject(
						new PaymentGatewayError(
							'Failed to tokenize payment data',
							'DISPATCH_ERROR',
							error,
						),
					);
				}
			}, 100);
		});
	}

	/**
	 * Validate form data before sending to Accept.js
	 */
	validateFormData(formData: PaymentMethodFormData): boolean {
		const errors: string[] = [];

		if (formData.paymentType === PaymentMethodTypeEnum.CREDIT_CARD) {
			// Credit Card validation
			if (
				!formData.cardNumber ||
				formData.cardNumber.replace(/\s/g, '').length < 13
			) {
				errors.push('Invalid card number');
			}

			if (
				!formData.expirationDate ||
				!/^(\d{2}\D(\d{2}|\d{4})|\d{4}|\d{6})$/.test(formData.expirationDate)
			) {
				errors.push('Invalid expiration date (MM/YY)');
			}

			if (!formData.securityCode || formData.securityCode.length < 3) {
				errors.push('Invalid security code');
			}

			if (!formData.nameOnCard?.trim()) {
				errors.push('Name on card is required');
			}
		} else if (formData.paymentType === PaymentMethodTypeEnum.ACH) {
			// eCheck validation
			if (!formData.routingNumber || !/^\d{9}$/.test(formData.routingNumber)) {
				errors.push('Routing number must be exactly 9 digits');
			}

			if (
				!formData.accountNumber?.trim() ||
				formData.accountNumber.trim().length < 4
			) {
				errors.push('Valid account number is required');
			}

			if (!formData.nameOnAccount?.trim()) {
				errors.push('Name on account is required');
			}

			if (
				!formData.accountType ||
				!['checking', 'savings'].includes(formData.accountType)
			) {
				errors.push('Valid account type (checking or savings) is required');
			}
		}

		// Common validation
		if (!formData.zipCode?.trim()) {
			errors.push('ZIP code is required');
		}

		if (errors.length > 0) {
			throw new PaymentGatewayError(
				`Validation failed: ${errors.join(', ')}`,
				'VALIDATION_ERROR',
			);
		}

		return true;
	}

	/**
	 * Clean up resources
	 */
	cleanup(): void {
		// Clear any sensitive data from memory
		this.config = null;
		this.isInitialized = false;
		// Note: We don't remove the script as it might be used by other components
	}

	/**
	 * Get configuration from backend
	 */
	private async getConfiguration(): Promise<PaymentGatewayConfig> {
		try {
			const response = await ApiService.apiGet<{
				success: boolean;
				data?: PaymentGatewayConfig;
				error?: string;
			}>('/payments/config/public');

			if (!response || !response.success || !response.data) {
				throw new Error(response?.error ?? 'No configuration received');
			}

			return response.data;
		} catch (error) {
			throw new PaymentGatewayError(
				'Failed to fetch payment gateway configuration',
				'CONFIG_ERROR',
				error,
			);
		}
	}

	/**
	 * Load Accept.js script dynamically
	 */
	private async loadAcceptJSScript(): Promise<void> {
		console.debug('[AuthorizeNet] loadAcceptJSScript called', {
			scriptLoaded: this.scriptLoaded,
			hasWindowAccept: Boolean(window.Accept),
		});

		if (this.scriptLoaded && window.Accept) {
			console.debug('[AuthorizeNet] Script already loaded, skipping');

			return;
		}

		if (!this.config) {
			throw new PaymentGatewayError(
				'Configuration required to load script',
				'NO_CONFIG',
			);
		}

		const scriptUrl =
			this.config.environment === 'production'
				? 'https://js.authorize.net/v1/Accept.js'
				: 'https://jstest.authorize.net/v1/Accept.js';

		console.debug('[AuthorizeNet] Loading script from:', scriptUrl);

		return new Promise((resolve, reject) => {
			// Check if script already exists
			const existingScript = document.querySelector(
				`script[src="${scriptUrl}"]`,
			);
			if (existingScript && window.Accept) {
				console.debug(
					'[AuthorizeNet] Found existing script with Accept available',
				);
				this.scriptLoaded = true;
				resolve();

				return;
			}

			if (existingScript) {
				console.debug(
					'[AuthorizeNet] Found existing script but Accept not available yet',
				);
			}

			const script = document.createElement('script');
			script.src = scriptUrl;
			script.async = true;

			script.onload = () => {
				console.debug(
					'[AuthorizeNet] Script onload fired, waiting for Accept to initialize...',
				);
				this.scriptLoaded = true;
				// Give Accept.js more time to fully initialize (Accept.js needs time to set up internal state)
				// Increased from 100ms to 500ms to ensure Accept.js is fully ready
				setTimeout(() => {
					console.debug(
						'[AuthorizeNet] After timeout, window.Accept:',
						Boolean(window.Accept),
					);
					if (window.Accept) {
						resolve();
					} else {
						console.error(
							'[AuthorizeNet] Accept.js loaded but window.Accept is undefined',
						);
						reject(
							new PaymentGatewayError(
								'Accept.js did not initialize properly',
								'SCRIPT_INIT_ERROR',
							),
						);
					}
				}, 500);
			};

			script.onerror = (event) => {
				console.error('[AuthorizeNet] Script failed to load:', event);
				reject(
					new PaymentGatewayError(
						'Failed to load Accept.js script',
						'SCRIPT_LOAD_ERROR',
					),
				);
			};

			document.head.appendChild(script);
		});
	}

	/**
	 * Remove empty/undefined values from an object
	 */
	private removeEmptyValues<T extends Record<string, any>>(data: T): T {
		const cleaned = { ...data };

		for (const key of Object.keys(cleaned)) {
			const value = cleaned[key];
			if (value === undefined || value === null || value === '') {
				delete cleaned[key];
			}
		}

		return cleaned;
	}
}
